<script setup>
import { computed, defineProps, ref, toRefs } from "vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import { track } from "jiffy-analytics";

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    required: false,
    default: ""
  }
});

const { src, alt } = toRefs(props);
const zoomPerClick = 0.25;
const maxZoomValue = 5;
const zoomedValue = ref(2);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const zoomPercentage = computed(() => (zoomedValue.value * 100).toFixed(0));

const zoomIn = () => {
  if (zoomedValue.value < maxZoomValue) {
    zoomedValue.value += zoomPerClick;
  } else {
    zoomedValue.value = maxZoomValue;
  }

  track("cart_preview_carousel_zoom_clicked", {
    zoom_level: zoomedValue.value
  });
};

const zoomOut = () => {
  if (zoomedValue.value > 1) {
    zoomedValue.value -= zoomPerClick;

    track("cart_preview_carousel_zoom_clicked", {
      zoom_level: zoomedValue.value
    });

    if (zoomedValue.value === 1) {
      translateX.value = 0;
      translateY.value = 0;
    }
  }
};

// eslint-disable-next-line max-lines-per-function
const handleDrag = event => {
  if (zoomedValue.value <= 1) return;

  event.preventDefault();

  const isTouchEvent = !!event.touches;
  const startX = isTouchEvent ? event.touches[0].clientX : event.clientX;
  const startY = isTouchEvent ? event.touches[0].clientY : event.clientY;
  const initialTranslateX = translateX.value;
  const initialTranslateY = translateY.value;

  const $imageEl = event.target;
  const containerRect = $imageEl.parentNode.getBoundingClientRect();

  const onDragMove = moveEvent => {
    const moveX = isTouchEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
    const moveY = isTouchEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
    const deltaX = (moveX - startX) / zoomedValue.value;
    const deltaY = (moveY - startY) / zoomedValue.value;
    let newTranslateX = initialTranslateX + deltaX;
    let newTranslateY = initialTranslateY + deltaY;

    // calculate scaled image dimensions
    const scaledWidth = $imageEl.offsetWidth * zoomedValue.value;
    const scaledHeight = $imageEl.offsetHeight * zoomedValue.value;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // set max translate values
    const maxX = Math.max(0, (scaledWidth - containerWidth) / (2 * zoomedValue.value));
    const maxY = Math.max(0, (scaledHeight - containerHeight) / (2 * zoomedValue.value));

    // clamp translate values
    newTranslateX = Math.max(-maxX, Math.min(maxX, newTranslateX));
    newTranslateY = Math.max(-maxY, Math.min(maxY, newTranslateY));

    translateX.value = newTranslateX;
    translateY.value = newTranslateY;
  };

  const onDragEnd = () => {
    isDragging.value = false;
    if (isTouchEvent) {
      window.removeEventListener("touchmove", onDragMove);
      window.removeEventListener("touchend", onDragEnd);
    } else {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
    }
  };

  isDragging.value = true;
  if (isTouchEvent) {
    window.addEventListener("touchmove", onDragMove);
    window.addEventListener("touchend", onDragEnd);
  } else {
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  }
};

const handlePinch = event => {
  event.preventDefault();

  // Minimum distance change (in pixels) to consider it a zoom action
  const pinchThreshold = 10;

  // More details about distance calculation: https://stackoverflow.com/questions/20916953/get-distance-between-two-points-in-canvas
  const getDistance = ([touch1, touch2]) =>
    Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);

  let initialPinchDistance = getDistance(event.touches);
  const onPinchMove = moveEvent => {
    const newDistance = getDistance(moveEvent.touches);
    const oldDistance = initialPinchDistance;

    if (newDistance > oldDistance + pinchThreshold) {
      zoomIn();
      initialPinchDistance = newDistance;
    } else if (newDistance < oldDistance - pinchThreshold) {
      zoomOut();
      initialPinchDistance = newDistance;
    }
  };

  const onPinchEnd = () => {
    window.removeEventListener("touchmove", onPinchMove);
    window.removeEventListener("touchend", onPinchEnd);
  };

  window.addEventListener("touchmove", onPinchMove);
  window.addEventListener("touchend", onPinchEnd);
};

const handleTouchStart = event => {
  if (event.touches.length === 2) {
    handlePinch(event);
  } else if (event.touches.length === 1) {
    handleDrag(event);
  }
};
</script>

<template>
  <div class="image-preview-modal-zoom">
    <img
      :src="src"
      :alt="alt"
      draggable="false"
      class="image-preview-modal-zoom__image"
      :class="{ 'image-preview-modal-zoom__image--dragging': isDragging }"
      :style="{
        '--zoom-value': zoomedValue.toFixed(2),
        '--translate-x': `${translateX}px`,
        '--translate-y': `${translateY}px`
      }"
      @mousedown="handleDrag"
      @touchstart="handleTouchStart"
    />

    <div class="image-preview-modal-zoom__controls">
      <div class="image-preview-modal-zoom__controls-control">
        <button type="button" class="image-preview-modal-zoom__controls-btn" @click="zoomOut">
          <span class="sr-only">Zoom out</span>
          <UseSvg
            class="image-preview-modal-zoom__controls-btn-icon--small-device"
            aria-hidden="true"
            icon="zoom-out-o"
          />
          <UseSvg
            class="image-preview-modal-zoom__controls-btn-icon--large-device"
            aria-hidden="true"
            icon="minus"
          />
        </button>

        <span class="image-preview-modal-zoom__controls-info">{{ `${zoomPercentage}%` }}</span>

        <button type="button" class="image-preview-modal-zoom__controls-btn" @click="zoomIn">
          <span class="sr-only">Zoom in</span>
          <UseSvg
            class="image-preview-modal-zoom__controls-btn-icon--small-device"
            aria-hidden="true"
            icon="zoom-in-o"
          />
          <UseSvg
            class="image-preview-modal-zoom__controls-btn-icon--large-device"
            aria-hidden="true"
            icon="plus"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.image-preview-modal-zoom {
  $this: &;

  &__controls {
    position: absolute;
    bottom: 8px;
    left: 8px;

    @include with-tablet() {
      bottom: 16px;
      right: 16px;
      left: auto;
    }

    &-control {
      height: 40px;
      border-radius: 8px;
      background-color: $white;
      border: 1px solid $gray-100;
      padding-inline: 4px;
      display: grid;
      grid-template-columns: 32px 32px;
      align-items: center;
      gap: 8px;
      box-shadow:
        0 4px 8px -2px rgba(18, 25, 38, 0.1),
        0 2px 4px -2px rgba(18, 25, 38, 0.36);

      @include with-tablet() {
        grid-template-columns: 32px 66px 32px;
      }
    }

    &-info,
    &-label {
      font-size: 13px;
      font-family: $font-family-inter;
      line-height: 16px;
      font-weight: 500;
      color: $gray-modern-700;
    }

    %divider {
      content: "";
      position: absolute;
      display: block;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 32px;
      background-color: $app-gray-modern-100;
    }

    &-label {
      position: relative;
      align-self: center;
      margin-bottom: 0;
      padding-right: 6px;

      &::before {
        @extend %divider;

        left: -7px;
      }
    }

    &-info {
      text-align: center;
      position: relative;
      display: none;

      @include with-tablet() {
        display: block;
      }

      &::before,
      &::after {
        @extend %divider;
      }

      &::before {
        left: 0;
      }

      &::after {
        right: 0;
      }
    }

    &-btn {
      width: 32px;
      height: 32px;
      display: grid;
      place-content: center;
      color: $app-gray-modern-600;
      position: relative;

      @include only-mobile() {
        &:first-child::after {
          content: "";
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: -4px;
          height: calc(100% - 8px);
          width: 1px;
          background-color: $gray-100;
        }

        &--as-icon::after {
          display: none;
        }
      }

      & > svg {
        width: 20px;
        height: 20px;
      }

      &-icon {
        &--small-device {
          @include with-large-desktop-md() {
            display: none !important;
          }
        }

        &--large-device {
          display: none !important;

          @include with-large-desktop-md() {
            display: block !important;
          }
        }
      }
    }
  }

  &__image,
  & {
    height: 100%;
    width: 100%;
  }

  & {
    overflow: hidden;
  }

  &__image {
    transform: scale(var(--zoom-value, 1)) translateX(var(--translate-x, 0))
      translateY(var(--translate-y, 0));
    transition: transform 150ms ease-in-out;
    will-change: transform;
    object-fit: contain;
    cursor: grab;

    &--dragging {
      cursor: grabbing;
      transition: none;
    }
  }
}

#tidio-chat-iframe {
  bottom: 0 !important;
}
</style>
